/**
 * formatFactValue — the ONE magnitude-aware display rounding for canonical
 * fact values in validator messages / repair overlays / scorer explanations.
 *
 * X-SED-5 established the rule in `siteEvidence.ts#formatCanonicalValue` (the
 * canonical prompt block): full-float precision in a reader-facing string
 * ("the only permitted value is 230.76923076923077 MW") is noise that also
 * seeds an unmeetable "exactly match" contract. This helper extracts the SAME
 * rule so the grounding validator's violation messages, the grounding repair
 * overlay, and the deterministic scorer's category explanations round
 * identically:
 *
 *   - integers render verbatim            (300      → "300")
 *   - |value| ≥ 10 rounds to 1 decimal    (1170.105… → "1170.1")
 *   - |value| < 10 rounds to 2 decimals   (5.1966…  → "5.2" — trailing zeros
 *     trimmed via Number(), so 5.20 → "5.2", 1.30 → "1.3")
 *
 * DISPLAY-ONLY: stores and matchers keep full precision; tolerance-based
 * numeric matching is unaffected. `siteEvidence.ts#formatCanonicalValue` is
 * deliberately left as-is (same rule, byte-stable output) to avoid churning
 * its test surface — if the rule ever changes, change BOTH.
 *
 * PURE. Never throws. Non-finite inputs degrade to `String(value)`.
 */
export function formatFactValue(value: number): string {
  if (!Number.isFinite(value)) return String(value);
  if (Number.isInteger(value)) return String(value);
  return String(Number(value.toFixed(Math.abs(value) >= 10 ? 1 : 2)));
}
